import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import useProjects from "../../Hooks/useProjects";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Spinner, Badge, Row, Col, Card, CardBody, CardHeader, } from "reactstrap";
import { formatIndianNumber } from "../../utils/indianNumberFormatter";

const ProjectDetails = () => {
  const { slug } = useParams();
  const { projectBySlug, fetchProjectBySlug, loading } = useProjects();

  useEffect(() => {
    if (slug) fetchProjectBySlug(slug);
  }, [slug]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner />
      </div>
    );
  }

  if (!projectBySlug) {
    return <div className="text-center py-5">Project not found</div>;
  }

  const { project, configurations, towers } = projectBySlug;

  const hasImages = project?.media?.images?.length > 0;
  const hasAmenities = project?.amenities?.length > 0;
  const hasConfigurations = configurations?.length > 0;
  const hasTowers = towers?.length > 0;

  return (
    <div className="page-content">

      {/* 🔥 BREADCRUMB */}
      <Breadcrumbs
        title="Projects"
        breadcrumbItem={project.projectName || "Project Details"}
      />

      <div className="container-fluid">

        {/* HERO CARD */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody>
            <Row>
              <Col md={8}>
                <h3 className="fw-bold mb-1"> <strong>ProjectName :</strong> {project.projectName}</h3>
                <p className="text-muted mb-2">
                  <strong>Builder:</strong> {project?.builderName?.name || "-"}
                </p>

                <div className="mb-2">
                  {project.projectType && (
                    <Badge color="primary" className="me-2 px-3 py-2">
                      {project.projectType}
                    </Badge>
                  )}
                  {project.projectStatus && (
                    <Badge color="dark" className="px-3 py-2">
                      {project.projectStatus}
                    </Badge>
                  )}
                </div>

                {(project?.location?.city || project?.location?.state) && (
                  <p className="text-muted mb-0">
                    <strong>Location:</strong>{" "}
                    {project?.location?.city || ""},{" "}
                    {project?.location?.state || ""}
                  </p>
                )}
              </Col>

              <Col md={4} className="text-md-end mt-3 mt-md-0">
                {(project?.priceRange?.min || project?.priceRange?.max) && (
                  <>
                    <p className="text-muted mb-1">
                      <strong>Price Range</strong>
                    </p>
                    <h5 className="text-success fw-bold mb-0">
                      ₹ {formatIndianNumber(project?.priceRange?.min)} - ₹ {formatIndianNumber(project?.priceRange?.max)}
                    </h5>
                  </>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* IMAGES */}
        {hasImages && (
          <Card className="border-0 shadow-sm mb-4">
            <CardHeader className="bg-white fw-semibold">
              Project Gallery
            </CardHeader>
            <CardBody>
              <Row>
                {project.media.images.slice(0, 6).map((img, i) => (
                  <Col md={4} key={i} className="mb-3">
                    <img
                      src={img}
                      alt="project"
                      className="img-fluid rounded"
                      style={{
                        height: "200px",
                        objectFit: "cover",
                        width: "100%",
                      }}
                    />
                  </Col>
                ))}
              </Row>
            </CardBody>
          </Card>
        )}

        {/* AMENITIES */}
        {hasAmenities && (
          <Card className="border-0 shadow-sm mb-4">
            <CardHeader className="bg-white fw-semibold">
              Amenities
            </CardHeader>
            <CardBody>
              <div className="d-flex flex-wrap">
                {project.amenities.map((a) => (
                  <Badge
                    key={a._id}
                    className="me-2 mb-2 px-3 py-2"
                    color="light"
                    style={{
                      border: "1px solid #e9ecef",
                      fontWeight: 500,
                    }}
                  >
                    {a.name}
                  </Badge>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* CONFIGURATIONS */}
        {hasConfigurations && (
          <Card className="border-0 shadow-sm mb-4">
            <CardHeader className="bg-white fw-semibold">
              Configurations
            </CardHeader>
            <CardBody>
              <Row>
                {configurations.map((c) => (
                  <Col md={4} key={c._id}>
                    <Card className="border-0 shadow-sm mb-3 h-100">
                      <CardBody>
                        <p className="text-muted mb-1">
                          <strong>Type</strong>
                        </p>
                        <h6 className="fw-bold">{c.type}</h6>

                        <p className="text-muted mb-1 mt-2">
                          <strong>Size</strong>
                        </p>
                        <p className="mb-1">{c.size} sqft</p>

                        <p className="text-muted mb-1 mt-2">
                          <strong>Price</strong>
                        </p>
                        <h6 className="text-success fw-bold mb-0">
                          ₹ {c.price}
                        </h6>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </CardBody>
          </Card>
        )}

        {/* TOWERS */}
        {hasTowers && (
          <Card className="border-0 shadow-sm mb-4">
            <CardHeader className="bg-white fw-semibold">
              Towers
            </CardHeader>
            <CardBody>
              <Row>
                {towers.map((t) => (
                  <Col md={4} key={t._id}>
                    <Card className="border-0 shadow-sm mb-3 h-100">
                      <CardBody>
                        <p className="text-muted mb-1">
                          <strong>Name</strong>
                        </p>
                        <h6 className="fw-bold">{t.name}</h6>

                        <p className="text-muted mb-1 mt-2">
                          <strong>Total Floors</strong>
                        </p>
                        <p className="mb-1">{t.totalFloors}</p>

                        <p className="text-muted mb-1 mt-2">
                          <strong>Total Units</strong>
                        </p>
                        <p className="mb-1">{t.totalUnits}</p>

                        <p className="text-muted mb-1 mt-2">
                          <strong>Available Units</strong>
                        </p>
                        <p className="text-success fw-semibold mb-0">
                          {t.availableUnits}
                        </p>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </CardBody>
          </Card>
        )}

      </div>
    </div>
  );
};

export default ProjectDetails;